import { useRef, useState, type FC } from 'react';
import { Box, Button } from '@mui/material';
import axios from 'axios';
import { PLAN_DETAILS, formatPrice, type PlanId } from 'Billing/Catalog';
import { usePricingCatalog } from 'Billing/usePricingCatalog';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import './paywall.css';

interface PaymentProps {
  setShowPaywall: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPayment: React.Dispatch<React.SetStateAction<boolean>>;
  setShowConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  planTypeSelected: string;
  exitAllowed: boolean;
  discountCodeValid: boolean | null;
  setDiscountCodeValid: React.Dispatch<React.SetStateAction<boolean | null>>;
  discountCode: string;
}

/** All new subscriptions use the same server-priced, authenticated hosted checkout. */
const Payment: FC<PaymentProps> = ({
  setShowPaywall,
  setShowPayment,
  planTypeSelected,
  discountCode,
  discountCodeValid,
}) => {
  const { currentAuthToken } = useAuthWrapperContext();
  const { catalog, loading, error: pricingError, reload } = usePricingCatalog();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const busy = useRef(false);
  const plan = catalog?.plans.find((item) => item.id === planTypeSelected);
  const details = PLAN_DETAILS[planTypeSelected as PlanId];
  const checkout = async () => {
    if (!plan || busy.current) return;
    busy.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const token = await currentAuthToken();
      const { data } = await axios.post<{ url: string }>(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/stripe/checkout-session`,
        {
          plan: plan.id,
          code: discountCodeValid ? discountCode.trim() : null,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 },
      );
      if (!data.url?.startsWith('https://checkout.stripe.com/'))
        throw new Error('Checkout unavailable');
      window.location.assign(data.url);
    } catch {
      setError('Unable to open checkout. Please try again.');
    } finally {
      busy.current = false;
      setSubmitting(false);
    }
  };
  return (
    <>
      <Box className="modal-outer" />
      <Box className="modal-inner2" role="dialog" aria-modal="true" aria-label="Confirm your plan">
        <h3 className="payment-title">{details?.title ?? 'Choose a plan'}</h3>
        <p className="paywall-desc-text">
          {plan && catalog
            ? `${formatPrice(plan.amountCents / 100, catalog.currency)} per ${details.frequency}`
            : loading
              ? 'Loading prices…'
              : 'Pricing unavailable'}
        </p>
        <p className="paywall-desc-text">
          Your recurring price, any coupon, and applicable tax will be confirmed at checkout.
        </p>
        {(error || pricingError) && (
          <p className="iap-error" role="alert">
            {error || pricingError}
          </p>
        )}
        {pricingError && <Button onClick={reload}>Retry prices</Button>}
        <Button onClick={checkout} disabled={!plan || submitting}>
          {submitting ? 'Opening checkout…' : 'Continue to secure checkout'}
        </Button>
        <Button
          onClick={() => {
            setShowPayment(false);
            setShowPaywall(true);
          }}
          disabled={submitting}
        >
          Back to plans
        </Button>
      </Box>
    </>
  );
};
export default Payment;
